import React, { useState, useMemo, Fragment } from 'react';
import { Avatar, IconButton, Menu, MenuItem, Tooltip } from '@material-ui/core';
import PowerIcon from '@material-ui/icons/PowerSettingsNew';
import VpnKeyRoundedIcon from '@material-ui/icons/VpnKeyRounded';
import PersonRoundedIcon from '@material-ui/icons/PersonRounded';
import DialpadRoundedIcon from '@material-ui/icons/DialpadRounded';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import { FEATURE } from '../../utils/feature';

const hasFeature = FEATURE();

const StyledMenu = withStyles({
  paper: {
    border: '2px solid var(--dark-primary-v2)',
  },
})((props) => (
  <Menu
    {...props}
  />
));

const useStyles = makeStyles(() => ({
  primary: {
    backgroundColor: 'var(--primary-v2)',
  },
}));

// eslint-disable-next-line no-unused-vars
const LEGACY_LOGIN_URL = process.env.REACT_APP_LOGOUT_URL;

/**
 * @param {{
 *    FirstName: string;
 *    LastName: string;
 * }} prescriber 
 * @returns {{
 *    avatarTitle: string;
 * }}
 */
export const useAvatarTitle = (prescriber) => {
  const avatarTitle = useMemo(() => {
    let title = '';
    
    if (prescriber.FirstName) {
      title += prescriber.FirstName[0];
    }

    if (prescriber.LastName) {
      title += prescriber.LastName[0];
    }

    return title;
  }, [prescriber]);

  return { avatarTitle };
};

/**
 * @param {{
 *    logo: string;
 *    message: string;
 *    avatarTitle: string;
 *    hasActions: boolean;
 *    handler: {
 *      route: () => void,
 *    };
 *    logOff: () => Promise<{}>;
 *    validateToken: () => Promise<{}>;
 * }} props 
 * @returns {React.JSX.Element}
 */
export const PageHeading = ({
  logo = '',
  message = '',
  avatarTitle = '',
  hasActions = false,

  logOff = async () => ({}),
  validateToken = async () => ({}),
  handler = { route: () => {} },
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const classes = useStyles();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * @description hide menu items
   */
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    /**
     * @description clear session on server
     */
    logOff();

    handleClose();

    /**
     * @description logs out locally
     */
    localStorage.removeItem('userData');
    localStorage.removeItem('userId');

    /**
     * @description route to login screen
     */
    window.open(LEGACY_LOGIN_URL, '_self');
    // handler.route('/');
  };

  const handleChangePassword = () => {
    handler.route('/change-password');
  };

  const handleChangePIN = () => {
    handler.route('/change-pin');
  };

  const handleViewProfile = () => {
    handler.route('/profile');
  };

  const AvatarComponent = new Map([
    [true, (
      <div className='patient-chart-profile flex gap-16 align-items-center'>
        <label className='patient-chart-profile-message font-18'>{message}</label>

        <div className='flex'>
          <Tooltip title='Click to open Menu' placement="top" arrow>
            <IconButton aria-label='avatar' size='small' onClick={handleClick}>
              <Avatar className={classes.primary}>{avatarTitle}</Avatar>
            </IconButton>
          </Tooltip>

          <StyledMenu
            id='hcp-menu'
            anchorEl={anchorEl}
            keepMounted
            getContentAnchorEl={null}
            elevation={0}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            {
              hasFeature.PROFILE && (
                <MenuItem className='flex gap-12 align-items-center min-width-140' onClick={handleViewProfile}>
                  <PersonRoundedIcon fontSize='small' />
                  <label className='pointer'>Profile</label>
                </MenuItem>
              )
            }
            {
              hasFeature.CHANGE_PASSWORD && (
                <MenuItem className='flex gap-12 align-items-center min-width-140' onClick={handleChangePassword}>
                  <VpnKeyRoundedIcon fontSize='small' />
                  <label className='pointer'>Change Password</label>
                </MenuItem>
              )
            }
            {
              hasFeature.CHANGE_PIN && (
                <MenuItem className='flex gap-12 align-items-center min-width-140' onClick={handleChangePIN}>
                  <DialpadRoundedIcon fontSize='small' />
                  <label className='pointer'>Change PIN</label>
                </MenuItem>
              )
            }
            <MenuItem className='flex gap-12 align-items-center min-width-140' onClick={handleLogout}>
              <PowerIcon fontSize='small' />
              <label className='pointer'>Logout</label>
            </MenuItem>
          </StyledMenu>
        </div>
      </div>
    )],
    [false, <Fragment />],
  ]);

  return (
    <div className='patient-chart-heading height-56 flex align-items-center justify-content-space-between p-l-6 p-r-6'>
      <div className='patient-chart-logo'>
        <img id='patient-chart-logo-element' className='height-30' src={logo} alt='logo' />
      </div>
  
      {AvatarComponent.get(hasActions)}
    </div>
  );
};

export default PageHeading;
